import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ClipboardList, CheckCircle2, Camera, CreditCard, PackageCheck, Truck, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { getPriorityActions, type PriorityAction, type ActionKind } from "@/lib/priority-actions.functions";

const ACTION_META: Record<ActionKind, { label: string; icon: typeof Camera; button: string }> = {
  review_photos: { label: "Photos to review", icon: Camera, button: "Review" },
  pay_now: { label: "Payment pending", icon: CreditCard, button: "Pay Now" },
  confirm_receipt: { label: "Confirm delivery", icon: PackageCheck, button: "Confirm Receipt" },
  submit_photos: { label: "Photos required", icon: Camera, button: "Submit Photos" },
  mark_shipped: { label: "Ready to ship", icon: Truck, button: "Mark Shipped" },
};

function formatTimeLeft(iso: string | null): string {
  if (!iso) return "";
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Overdue";
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return `${Math.max(1, Math.floor(diff / 60_000))}m left`;
  if (h < 48) return `${h}h left`;
  return `${Math.floor(h / 24)}d left`;
}

function actionHash(kind: ActionKind): string {
  switch (kind) {
    case "review_photos":
    case "submit_photos":
      return "photo-review";
    case "mark_shipped":
      return "photo-review";
    case "confirm_receipt":
      return "photo-review";
    case "pay_now":
      return "photo-review";
    default:
      return "";
  }
}

function DetailLink({
  a,
  onClose,
  className,
  children,
}: {
  a: PriorityAction;
  onClose: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  const hash = actionHash(a.kind) || undefined;
  if (a.detail_kind === "order" || a.detail_kind === "checkout") {
    return (
      <Link to="/orders/$id" params={{ id: a.detail_id }} hash={hash} onClick={onClose} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <Link to="/transaction/$id" params={{ id: a.detail_id }} onClick={onClose} className={className}>
      {children}
    </Link>
  );
}

function ActionRow({ a, onClose }: { a: PriorityAction; onClose: () => void }) {
  const meta = ACTION_META[a.kind];
  const Icon = meta.icon;
  const timeLeft = formatTimeLeft(a.deadline_at);
  const overdue = timeLeft === "Overdue";
  return (
    <DetailLink
      a={a}
      onClose={onClose}
      className="flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-muted/50"
    >
      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md border border-border bg-panel">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold">{a.card_name}</span>
        <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
          {a.counterparty_username ? `@${a.counterparty_username} · ` : ""}
          {meta.label}
          {timeLeft && (
            <span className={overdue ? "ml-1 text-destructive" : "ml-1"}>· {timeLeft}</span>
          )}
        </span>
      </span>
      <span className="ml-2 shrink-0 rounded-md bg-primary/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
        {meta.button}
      </span>
    </DetailLink>
  );
}

export function FulfillmentCartBadge() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const fetchFn = useServerFn(getPriorityActions);
  const { data } = useQuery({
    queryKey: ["priority-actions", user?.id ?? null],
    queryFn: () => fetchFn(),
    enabled: !!user,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });

  const total = data?.total ?? 0;
  const buying = (data?.buying ?? []).slice(0, 5);
  const selling = (data?.selling ?? []).slice(0, 5);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Fulfilment cart"
        className="relative grid h-10 w-10 place-items-center rounded-md border border-border bg-panel hover:bg-muted"
      >
        <ClipboardList className="h-4 w-4" />
        {total > 0 && (
          <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-50 w-[24rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-border bg-panel shadow-2xl">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="font-display text-sm font-bold">Fulfilment Cart</div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {total} pending
            </span>
          </div>

          {total === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              <div className="text-sm font-semibold">All caught up.</div>
              <p className="text-xs text-muted-foreground">
                No pending actions on your buying or selling activity.
              </p>
            </div>
          ) : (
            <div className="max-h-[28rem] overflow-y-auto">
              <div className="border-b border-border">
                <div className="flex items-center justify-between px-3 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <span>Buying</span>
                  <span>{buying.length}</span>
                </div>
                {buying.length === 0 ? (
                  <div className="px-3 py-3 text-[11px] text-muted-foreground">
                    Nothing needs your attention as a buyer.
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {buying.map((a) => (
                      <ActionRow key={a.id} a={a} onClose={() => setOpen(false)} />
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between px-3 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <span>Selling</span>
                  <span>{selling.length}</span>
                </div>
                {selling.length === 0 ? (
                  <div className="px-3 py-3 text-[11px] text-muted-foreground">
                    Nothing needs your attention as a seller.
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {selling.map((a) => (
                      <ActionRow key={a.id} a={a} onClose={() => setOpen(false)} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <Link
            to="/dashboard"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-1.5 border-t border-border py-2.5 text-xs font-semibold text-primary hover:bg-muted/40"
          >
            View Full Dashboard <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}
